import {put, call, all} from 'redux-saga/effects';
import CourtDeadlineRule from "../../entities/courtDeadlineRule";
import * as Actions from "../actions/courtDeadlineRuleActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CourtDeadlineRuleAction = {
    courtDeadlineRuleId: string;
    courtDeadlineRule: CourtDeadlineRule;
    courtDeadlineRules: CourtDeadlineRule[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCourtDeadlineRule(action: CourtDeadlineRuleAction) {
	const session = tabSession.captureGeneration();
	try {
		const courtDeadlineRule: CourtDeadlineRule = yield call(api.findOne, 'courtDeadlineRule', action.courtDeadlineRuleId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_FETCH_ONE_SUCCESS, courtDeadlineRuleId: action.courtDeadlineRuleId, courtDeadlineRule, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURTDEADLINERULE_FETCH_ONE_FAILURE, courtDeadlineRuleId: action.courtDeadlineRuleId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CourtDeadlineRuleAction} action
 */
export function* saveCourtDeadlineRule(action: CourtDeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ COURTDEADLINERULE_SAVE saga', action.courtDeadlineRule?.id);

		if (action.courtDeadlineRule === undefined || action.courtDeadlineRule === null) {
			console.error('saveCourtDeadlineRule received a null');
			throw new Error('saveCourtDeadlineRule received a null');
		}

		if (!(action.courtDeadlineRule instanceof CourtDeadlineRule)) {
			action.courtDeadlineRule = new CourtDeadlineRule(action.courtDeadlineRule);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.courtDeadlineRule.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.courtDeadlineRule);
			const addResult = yield call(idb.add, 'courtDeadlineRule', action.courtDeadlineRule);
			if (addResult.isErr()) {
				console.error(`⮂ saveCourtDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCourtDeadlineRuleSave(action.courtDeadlineRule, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.COURTDEADLINERULE_SAVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.COURTDEADLINERULE_SAVE, courtDeadlineRule: action.courtDeadlineRule, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCourtDeadlineRuleSave(action.courtDeadlineRule, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.COURTDEADLINERULE_SAVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CourtDeadlineRuleAction} action
 * @param {CourtDeadlineRule[]} action.courtDeadlineRules
 */
export function* saveManyCourtDeadlineRules(action: CourtDeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ COURTDEADLINERULE_SAVE_MANY saga', action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule.id).join(','));

        if (!Array.isArray(action.courtDeadlineRules)) {
            console.error('saveManyCourtDeadlineRules expected an array, received', action.courtDeadlineRules);
            throw new Error('saveManyCourtDeadlineRules expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCourtDeadlineRule), under these queue keys
		const dispatchedAddedOns: number[] = action.courtDeadlineRules.map(courtDeadlineRule => courtDeadlineRule?.addedOn);
		const revertAll = () => action.courtDeadlineRules.map((courtDeadlineRule, i) =>
			put(Actions.revertCourtDeadlineRuleSave(courtDeadlineRule, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: CourtDeadlineRule[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.courtDeadlineRules.length; i++) {
				const courtDeadlineRule = action.courtDeadlineRules[i];
				courtDeadlineRule.persisted = false;
				ensureOperationId(courtDeadlineRule);
				toSave.push(courtDeadlineRule);
			}

			const addResult = yield call(idb.add, 'courtDeadlineRule', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCourtDeadlineRules: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.COURTDEADLINERULE_SAVE_MANY_FAILURE, courtDeadlineRules: action.courtDeadlineRules, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.COURTDEADLINERULE_SAVE_MANY, courtDeadlineRules: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.COURTDEADLINERULE_SAVE_MANY_FAILURE, courtDeadlineRules: action.courtDeadlineRules, status: 500, err});

			console.error(`Error saving ${action.courtDeadlineRules.length} courtDeadlineRules to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CourtDeadlineRuleAction} action
 */
export function* removeCourtDeadlineRule(action: CourtDeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.courtDeadlineRule === undefined || action.courtDeadlineRule === null || !(action.courtDeadlineRule instanceof CourtDeadlineRule)) {
			console.error('removeCourtDeadlineRule expected type CourtDeadlineRule, received', action.courtDeadlineRule);
			reportQueued(false);
			throw new Error('removeCourtDeadlineRule expected type CourtDeadlineRule');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.courtDeadlineRule.status = 'D';
		action.courtDeadlineRule.addedOn = newHRTimestamp();
		action.courtDeadlineRule.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.courtDeadlineRule);
		const addResult = yield call(idb.add, 'courtDeadlineRule', action.courtDeadlineRule);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCourtDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.COURTDEADLINERULE_REMOVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_REMOVE, courtDeadlineRule: action.courtDeadlineRule});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURTDEADLINERULE_REMOVE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this CourtDeadlineRule.
	*
	* @param {CourtDeadlineRuleAction} action
	*/
export function* restoreCourtDeadlineRule(action: CourtDeadlineRuleAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCourtDeadlineRule expected getCurrentUserId() to return a value');
        return;
    }

	if (action.courtDeadlineRule === undefined || action.courtDeadlineRule === null) {
        console.error('restoreCourtDeadlineRule expected a CourtDeadlineRule, received null');
        throw new Error('restoreCourtDeadlineRule expected a CourtDeadlineRule, received null');
    }
    else if (!(action.courtDeadlineRule instanceof CourtDeadlineRule)) action.courtDeadlineRule = new CourtDeadlineRule(action.courtDeadlineRule); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.courtDeadlineRule.status = 'U';
		action.courtDeadlineRule.addedOn = newHRTimestamp();
		action.courtDeadlineRule.addedBy = userId;
		action.courtDeadlineRule.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.courtDeadlineRule);
		const addResult = yield call(idb.add, 'courtDeadlineRule', action.courtDeadlineRule);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCourtDeadlineRule: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.COURTDEADLINERULE_RESTORE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.COURTDEADLINERULE_RESTORE, courtDeadlineRule: action.courtDeadlineRule});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.COURTDEADLINERULE_RESTORE_FAILURE, courtDeadlineRule: action.courtDeadlineRule, status: 500, err});

		console.error('Error restoring from database', err);
	}
}